import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';
import * as focusTrap from 'focus-trap';
import { keepScrollbarY } from '../utils/_scrollUtils';

class oSidebar extends HTMLDivElement {
    constructor() {
        super();
        this.buttonClose = this.querySelector('.close');
        this.overlayClose = this.querySelector('[is="a-overlay"]');
        this.slidePanel = this.querySelector('.slide-panel');
        this.slideFooter = this.querySelector('.footer');
        this.preventOverlayClose = this.dataset.preventOverlayClose !== undefined;
        this.hasCustomWidth = this.dataset.hasCustomWidth !== undefined
            ? this.dataset.hasCustomWidth
            : false;
        this.hasFocusableElement = this.querySelectorAll('button, a, textarea, input, select').length > 0;

        /**
         * List of elements that you don't want the touch/scroll gesture to
         * go through and scroll the body page behind (only for iOS).
         * Used by the `tua-body-scroll-lock` library.
         * Note: `overscroll-behavior: none` is not a working alternative (Tested on iOS 17.2).
         */
        this.bodyScrollElements = [
            this.slidePanel,
        ];

        // bind this
        this.actionKeyboard = this.actionKeyboard.bind(this);
        this.setSidebarTrigger = this.setSidebarTrigger.bind(this);
        this.onOpenSlidebar = this.onOpenSlidebar.bind(this);
        this.onCloseSlidebar = this.onCloseSlidebar.bind(this);
        this.actionKeyboard = this.actionKeyboard.bind(this);
        this.setToggleSidebar = this.setToggleSidebar.bind(this);
        this.setSidebarContent = this.setSidebarContent.bind(this);
        this.phonePrefixDeactivateTrap = this.phonePrefixDeactivateTrap.bind(this);
        this.phonePrefixActivateTrap = this.phonePrefixActivateTrap.bind(this);
    }

    connectedCallback() {
        this.handleUrlParams();
        document.addEventListener('click', this.setSidebarTrigger);
        document.addEventListener('oSidebar:toggle', this.setToggleSidebar);
        document.addEventListener('oSidebar:setContent', this.setSidebarContent);
        document.addEventListener('oSidebar:onCloseSlidebar', this.onCloseSlidebar);

        if (this.buttonClose) {
            this.buttonClose.addEventListener('click', this.onCloseSlidebar);
        }
        if (this.hasCustomWidth) this.style.setProperty('--slidePanelMaxWidth', `${this.hasCustomWidth}px`);

        this.trap = focusTrap.createFocusTrap(this, {
            initialFocus: this.buttonClose || undefined,
            allowOutsideClick: true,
        });

        // Fix native scrollbar toggling loop on Chrome
        this.scrollObserver = keepScrollbarY(this.slidePanel);
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.setSidebarTrigger);
        document.removeEventListener('oSidebar:toggle', this.setToggleSidebar);
        document.removeEventListener('oSidebar:setContent', this.setSidebarContent);
    }

    handleUrlParams() {
        const querystring = new URLSearchParams(window.location.search);
        const paramLayer = querystring.get('layer');
        if (paramLayer && this.id === `o-sidebar-${paramLayer}`) {
            this.onOpenSlidebar();
        }
    }

    setSidebarTrigger(e) {
        const btnTarget = e.target.closest('[data-target]');

        if (btnTarget && btnTarget.hasAttribute('data-target') && btnTarget.dataset.target === `#${this.id}`) {
            this.onOpenSlidebar();
        }
    }

    phonePrefixDeactivateTrap() {
        this.trap.deactivate();
        unlock(this.bodyScrollElements);
    }

    phonePrefixActivateTrap() {
        this.trap.activate();
        lock(this.bodyScrollElements);
    }

    setToggleSidebar(e) {
        if (e.detail && e.detail.sidebarId === this.id) {
            if (e.detail.open) {
                this.onOpenSlidebar();
            } else {
                this.onCloseSlidebar();
            }
        }
    }

    setSidebarContent(e) {
        if (e.detail && e.detail.sidebarId === this.id && e.detail.sidebarContent) {
            const slidePanelContent = this.querySelector(e.detail.selector);
            if (slidePanelContent) {
                slidePanelContent.innerHTML = e.detail.sidebarContent;
                if (e.detail.openSidebar) {
                    this.onOpenSlidebar();
                }
            }
        }
    }

    onOpenSlidebar() {
        const isClosed = !this.getAttribute('aria-expanded') || this.getAttribute('aria-expanded') === 'false';
        if (isClosed) {
            this.slidePanel.setAttribute('aria-hidden', false);
            this.setAttribute('tabindex', 0);
            this.setAttribute('aria-expanded', true);

            if (document.querySelectorAll('[is="o-sidebar"][aria-expanded="true"], [is="o-sidebar-ajax"][aria-expanded="true"]').length > 1) { // check if another sidebar is already open
                this.classList.add('another-sidebar-already-active');
            }
            lock(this.bodyScrollElements);
            if (this.hasFocusableElement) this.trap.activate();

            if (this.slideFooter !== null) {
                const footerHeight = this.slideFooter.scrollHeight;
                this.style.setProperty('--slide-footer-height', `${footerHeight}px`);
            } else {
                this.style.setProperty('--slide-footer-height', '0px');
            }
            this.overlayClose.classList.toggle('css-show');
            window.addEventListener('keydown', this.actionKeyboard);

            // if phone prefix is open desactive focus trap + unlock scroll
            document.body.addEventListener('open:countrydropdown', this.phonePrefixDeactivateTrap);

            // if phone prefix is closed reactivate focus trap + lock scroll
            document.body.addEventListener('close:countrydropdown', this.phonePrefixActivateTrap);
        } else {
            this.trap.deactivate();
            if (this.hasFocusableElement) this.trap.activate();
        }
        document.dispatchEvent(new CustomEvent('oSidebar:open', { detail: { sidebarId: this.id, sidebar: this } }));
    }

    onCloseSlidebar() {
        clearBodyLocks(); // make sure to clean all side effect of multiple lock called
        setTimeout(() => { this.classList.remove('another-sidebar-already-active'); }, 200);// remove the class after closing animation if it's here
        this.trap.deactivate();
        this.overlayClose.classList.remove('css-show');
        this.slidePanel.setAttribute('aria-hidden', true);
        this.setAttribute('tabindex', -1);
        this.setAttribute('aria-expanded', false);
        document.dispatchEvent(new CustomEvent('oSidebar:close', { detail: { sidebarId: this.id, sidebar: this } }));

        document.body.removeEventListener('close:countrydropdown', this.phonePrefixActivateTrap);
        document.body.removeEventListener('open:countrydropdown', this.phonePrefixActivateTrap);

        window.removeEventListener('keydown', this.actionKeyboard);
    }

    actionKeyboard(event) {
        if (event.code === 'Escape') {
            if (document.querySelector('[is="m-popin"][aria-hidden="false"]')) {
                return;
            }
            if (document.querySelectorAll('[is="o-sidebar"][aria-expanded="true"], [is="o-sidebar-ajax"][aria-expanded="true"]').length > 1) { // check if another sidebar is already open
                if (this.classList.contains('another-sidebar-already-active')) { // only close sidebar if it's the 2nd one
                    this.onCloseSlidebar();
                }
            } else {
                this.onCloseSlidebar();
            }
        }
    }
}

customElements.define('o-sidebar', oSidebar, {
    extends: 'div',
});
